import { __decorate, __metadata } from "tslib";
import { dispatchEvent, observe, translate } from '@1inch-community/core/lit-utils';
import { RateSource } from '@1inch-community/models';
import { isNativeToken } from '@1inch-community/sdk/chain';
import '@1inch-community/ui-components/button';
import '@1inch-community/ui-components/text-animate';
import { html, LitElement } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { combineLatest, debounceTime, defer, distinctUntilChanged, map, merge, of, shareReplay, startWith, Subject, switchMap, tap, withLatestFrom, } from 'rxjs';
import { CacheActivePromise } from '@1inch-community/core/decorators';
import { lazyAppContextConsumer, lazyConsumer } from '@1inch-community/core/lazy';
import { BigFloat } from '@1inch-community/core/math';
import { JsonParser } from '@1inch-community/core/storage';
import { SwapContextToken } from '@1inch-community/sdk/swap';
import { textAnimateHover } from '@1inch-community/ui-components/text-animate';
import { createRef, ref } from 'lit/directives/ref.js';
import { isAddress } from 'viem';
import { swapButtonStyle } from './swap-button.style';
var SwapButtonState;
(function (SwapButtonState) {
    SwapButtonState[SwapButtonState["readyToSwap"] = 0] = "readyToSwap";
    SwapButtonState[SwapButtonState["readyToSwapLoading"] = 1] = "readyToSwapLoading";
    SwapButtonState[SwapButtonState["needChangeChainInWallet"] = 2] = "needChangeChainInWallet";
    SwapButtonState[SwapButtonState["walletNotConnected"] = 3] = "walletNotConnected";
    SwapButtonState[SwapButtonState["unselectedSourceToken"] = 4] = "unselectedSourceToken";
    SwapButtonState[SwapButtonState["unselectedDestinationToken"] = 5] = "unselectedDestinationToken";
    SwapButtonState[SwapButtonState["zeroAmount"] = 6] = "zeroAmount";
    SwapButtonState[SwapButtonState["exceedingMaximumAmount"] = 7] = "exceedingMaximumAmount";
    SwapButtonState[SwapButtonState["rateNotExist"] = 8] = "rateNotExist";
    SwapButtonState[SwapButtonState["checkAllowance"] = 9] = "checkAllowance";
    SwapButtonState[SwapButtonState["lowAllowanceNeedApprove"] = 10] = "lowAllowanceNeedApprove";
    SwapButtonState[SwapButtonState["lowAllowanceNeedPermit"] = 11] = "lowAllowanceNeedPermit";
    SwapButtonState[SwapButtonState["wrapNativeToken"] = 12] = "wrapNativeToken";
    SwapButtonState[SwapButtonState["updateSwapData"] = 13] = "updateSwapData";
    SwapButtonState[SwapButtonState["waitingApproveTransaction"] = 14] = "waitingApproveTransaction";
    SwapButtonState[SwapButtonState["permitInWallet"] = 15] = "permitInWallet";
    SwapButtonState[SwapButtonState["approveInWallet"] = 16] = "approveInWallet";
})(SwapButtonState || (SwapButtonState = {}));
const showLoader = new Set([
    SwapButtonState.readyToSwapLoading,
    SwapButtonState.checkAllowance,
    SwapButtonState.permitInWallet,
    SwapButtonState.approveInWallet,
    SwapButtonState.updateSwapData,
    SwapButtonState.waitingApproveTransaction,
]);
const skipCheck = new Set([SwapButtonState.readyToSwapLoading]);
const textRecord = {
    [SwapButtonState.readyToSwap]: () => translate('widgets.swap-form.swap-button.confirm-swap'),
    [SwapButtonState.readyToSwapLoading]: () => translate('widgets.swap-form.swap-button.confirm-swap'),
    [SwapButtonState.needChangeChainInWallet]: () => translate(''),
    [SwapButtonState.walletNotConnected]: () => translate('widgets.swap-form.swap-button.connect-wallet'),
    [SwapButtonState.unselectedSourceToken]: () => translate('widgets.swap-form.swap-button.select-source-token'),
    [SwapButtonState.unselectedDestinationToken]: () => translate('widgets.swap-form.swap-button.select-destination-token'),
    [SwapButtonState.exceedingMaximumAmount]: (params) => translate('widgets.swap-form.swap-button.insufficient-balance', params),
    [SwapButtonState.rateNotExist]: () => translate('widgets.swap-form.swap-button.no-liquidity-for-swap'),
    [SwapButtonState.checkAllowance]: () => translate('widgets.swap-form.swap-button.check-allowance'),
    [SwapButtonState.lowAllowanceNeedApprove]: () => translate('widgets.swap-form.swap-button.approve-and-swap'),
    [SwapButtonState.lowAllowanceNeedPermit]: () => translate('widgets.swap-form.swap-button.permit-and-swap'),
    [SwapButtonState.wrapNativeToken]: () => translate('widgets.swap-form.swap-button.native-token-not-supported'),
    [SwapButtonState.updateSwapData]: () => translate('widgets.swap-form.swap-button.update-swap-data'),
    [SwapButtonState.waitingApproveTransaction]: () => translate('widgets.swap-form.swap-button.waiting-approve-transaction'),
    [SwapButtonState.permitInWallet]: () => translate('widgets.swap-form.swap-button.wait-wallet-response'),
    [SwapButtonState.approveInWallet]: () => translate('widgets.swap-form.swap-button.wait-wallet-response'),
    [SwapButtonState.zeroAmount]: () => translate('widgets.swap-form.swap-button.enter-amount-to-swap'),
};
const textHoverRecord = {
    [SwapButtonState.exceedingMaximumAmount]: (params) => translate('widgets.swap-form.swap-button.set-max', params),
};
const clickHandlers = {
    [SwapButtonState.readyToSwap]: async (button) => {
        button.buttonState = SwapButtonState.readyToSwapLoading;
        button.updateView$.next();
        const snapshot = await button.context.value.getSnapshot();
        if (snapshot) {
            dispatchEvent(button, 'confirmSwap', snapshot);
        }
    },
    [SwapButtonState.readyToSwapLoading]: () => Promise.resolve(),
    [SwapButtonState.needChangeChainInWallet]: () => Promise.resolve(),
    [SwapButtonState.walletNotConnected]: (button) => dispatchEvent(button, 'connectWallet', null),
    [SwapButtonState.unselectedSourceToken]: (button) => dispatchEvent(button, 'openTokenSelector', 'source'),
    [SwapButtonState.unselectedDestinationToken]: (button) => dispatchEvent(button, 'openTokenSelector', 'destination'),
    [SwapButtonState.exceedingMaximumAmount]: (button) => button.context.value.setMaxAmount(),
    [SwapButtonState.rateNotExist]: () => Promise.resolve(),
    [SwapButtonState.checkAllowance]: () => Promise.resolve(),
    [SwapButtonState.lowAllowanceNeedApprove]: () => Promise.resolve(),
    [SwapButtonState.lowAllowanceNeedPermit]: () => Promise.resolve(),
    [SwapButtonState.wrapNativeToken]: () => Promise.resolve(),
    [SwapButtonState.updateSwapData]: () => Promise.resolve(),
    [SwapButtonState.waitingApproveTransaction]: () => Promise.resolve(),
    [SwapButtonState.permitInWallet]: () => Promise.resolve(),
    [SwapButtonState.approveInWallet]: () => Promise.resolve(),
    [SwapButtonState.zeroAmount]: () => Promise.resolve(),
};
let SwapButtonElement = class SwapButtonElement extends LitElement {
    constructor() {
        super(...arguments);
        this.mobileView = false;
        this.context = lazyConsumer(this, { context: SwapContextToken });
        this.applicationContext = lazyAppContextConsumer(this);
        this.buttonState = SwapButtonState.readyToSwap;
        this.buttonRef = createRef();
        this.updateView$ = new Subject();
        this.connectedWalletAddress$ = defer(() => this.context.value.connectedWalletAddress$);
        this.sourceToken$ = defer(() => this.context.value.getTokenByType('source'));
        this.destinationToken$ = defer(() => this.context.value.getTokenByType('destination'));
        this.chainId$ = defer(() => this.context.value.chainId$);
        this.sourceTokenAmount$ = defer(() => this.context.value.getTokenRawAmountByType('source'));
        this.rate$ = defer(() => this.context.value.rate$);
        this.loading$ = defer(() => this.context.value.loading$);
        this.block$ = this.chainId$.pipe(switchMap((chainId) => chainId ? this.applicationContext.value.onChain.getBlockEmitter(chainId) : []));
        this.exceedingMaximumAmount$ = combineLatest([
            this.connectedWalletAddress$,
            this.sourceToken$,
            this.sourceTokenAmount$.pipe(startWith(BigFloat.zero())),
            merge(this.chainId$, this.block$).pipe(startWith(null)),
        ]).pipe(switchMap(async ([wallet, sourceToken, amount]) => {
            if (!wallet || !sourceToken || amount?.isZero())
                return false;
            const balance = await this.context.value.getMaxAmount();
            if (balance.isZero())
                return true;
            return !amount || balance.lt(amount);
        }), shareReplay({ bufferSize: 1, refCount: true }));
        this.buttonStatus$ = combineLatest([
            this.connectedWalletAddress$,
            this.sourceToken$,
            this.destinationToken$,
            this.exceedingMaximumAmount$,
            this.sourceTokenAmount$.pipe(startWith(BigFloat.zero())),
            this.chainId$,
            this.rate$.pipe(startWith(null)),
            this.loading$,
            // update status streams
            merge(this.block$, this.updateView$).pipe(startWith(null)),
        ]).pipe(debounceTime(10), switchMap((params, index) => {
            const [walletAddress, srcToken, dstToken, exceedingMaximumAmount, amount, chainId, rate, loading,] = params;
            if (skipCheck.has(this.buttonState)) {
                return of(this.buttonState);
            }
            return this.updateButtonState(index === 0, chainId, walletAddress, srcToken, dstToken, amount, rate, exceedingMaximumAmount, loading);
        }), map(() => this.buttonState), tap((state) => this.applicationContext.value.storage.set('fusion_swap_button_state', state)), startWith(this.buttonState), debounceTime(0), distinctUntilChanged(), shareReplay({ refCount: true, bufferSize: 1 }));
        this.buttonText$ = this.buttonStatus$.pipe(withLatestFrom(this.sourceToken$), map(([state, sourceToken]) => {
            return textRecord[state]({ symbol: sourceToken?.symbol ?? '' });
        }));
        this.buttonHoverText$ = this.buttonStatus$.pipe(withLatestFrom(this.sourceToken$), map(([state, sourceToken]) => {
            return textHoverRecord[state]?.({ symbol: sourceToken?.symbol ?? '' });
        }));
        this.buttonLoading$ = this.buttonStatus$.pipe(map((state) => {
            return showLoader.has(state);
        }));
    }
    static { this.tagName = 'inch-swap-button'; }
    static { this.styles = swapButtonStyle; }
    // private readonly calculateButtonStatus$: Observable<SwapButtonState> = combineLatest([
    //   this.connectedWalletAddress$,
    //   this.sourceToken$,
    //   this.destinationToken$,
    //   this.exceedingMaximumAmount$,
    //   this.sourceTokenAmount$.pipe(startWith(BigFloat.zero())),
    //   this.chainId$,
    //   this.rate$.pipe(startWith(null)),
    //   this.loading$,
    //
    //   // update status streams
    //   merge(this.block$, this.updateView$).pipe(startWith(null)),
    // ]).pipe(
    //   map((params, index) => {
    //     const [
    //       walletAddress,
    //       srcToken,
    //       dstToken,
    //       exceedingMaximumAmount,
    //       amount,
    //       chainId,
    //       rate,
    //       loading,
    //     ] = params
    //     if (!isChainId(chainId)) {
    //       return SwapButtonState.needChangeChainInWallet
    //     }
    //     if (walletAddress === null) {
    //       return SwapButtonState.walletNotConnected
    //     }
    //     if (srcToken === null) {
    //       return SwapButtonState.unselectedSourceToken
    //     }
    //     if (dstToken === null) {
    //       return SwapButtonState.unselectedDestinationToken
    //     }
    //     if (!amount || amount.isZero()) {
    //       return SwapButtonState.zeroAmount
    //     }
    //     if (exceedingMaximumAmount) {
    //       return SwapButtonState.exceedingMaximumAmount
    //     }
    //     if (loading && (index === 0 || rate === null)) {
    //       return SwapButtonState.updateSwapData
    //     }
    //     if (rate === null) {
    //       return SwapButtonState.rateNotExist
    //     }
    //     if (isNativeToken(srcToken.address)) {
    //       return SwapButtonState.wrapNativeToken
    //     }
    //     if (this.isNeedCheckAllowance()) {
    //       this.lastPermitCheckToken = srcToken
    //       return SwapButtonState.checkAllowance
    //     }
    //     return SwapButtonState.readyToSwap
    //   }),
    //   distinctUntilChanged(),
    //   debounceTime(0),
    //   withLatestFrom(this.chainId$, this.connectedWalletAddress$, this.sourceToken$),
    //   switchMap(async ([state, chainId, walletAddress, srcToken]) => {
    //     if (state !== SwapButtonState.readyToSwap) {
    //       return state
    //     }
    //     if (!chainId || !walletAddress || !srcToken) throw new Error('')
    //     this.buttonState = SwapButtonState.checkAllowance
    //     const contract = getOneInchRouterV6ContractAddress(chainId)
    //     const allowance = await this.applicationContext.value.onChain.getAllowance(
    //       chainId,
    //       srcToken.address,
    //       walletAddress,
    //       contract
    //     )
    //     const amount = await firstValueFrom(this.sourceTokenAmount$)
    //     if (amount && allowance < amount) {
    //       return SwapButtonState.lowAllowanceNeedApprove
    //     }
    //     return state
    //   })
    // )
    connectedCallback() {
        super.connectedCallback();
        const state = this.applicationContext.value.storage.get('fusion_swap_button_state', JsonParser) ?? this.buttonState;
        if (!skipCheck.has(state)) {
            this.buttonState = state;
        }
    }
    render() {
        const size = this.mobileView ? 'xl' : 'xxl';
        return html `
      <inch-button
        ${ref(this.buttonRef)}
        @click="${() => this.onClickSwapButton()}"
        .loader="${observe(this.buttonLoading$, false)}"
        type="secondary"
        size="${size}"
        fullSize
      >
        <inch-text-animate
          ${textAnimateHover({
            target: this.buttonRef,
            text$: this.buttonText$,
            hoverText$: this.buttonHoverText$,
        })}
          disabledHostTransition
        ></inch-text-animate>
      </inch-button>
    `;
    }
    async updateButtonState(isFirstCall, walletChainId, walletAddress, sourceToken, destinationToken, sourceTokenAmount, rate, exceedingMaximumAmount, loading) {
        const state = this.calculateButtonStateSimple(isFirstCall, walletChainId, walletAddress, sourceToken, destinationToken, sourceTokenAmount, rate, exceedingMaximumAmount, loading);
        this.buttonState = state;
    }
    calculateButtonStateSimple(isFirstCall, walletChainId, walletAddress, sourceToken, destinationToken, sourceTokenAmount, rate, exceedingMaximumAmount, loading) {
        if (walletAddress === null || !isAddress(walletAddress)) {
            return SwapButtonState.walletNotConnected;
        }
        if (sourceToken === null) {
            return SwapButtonState.unselectedSourceToken;
        }
        if (destinationToken === null) {
            return SwapButtonState.unselectedDestinationToken;
        }
        if (sourceTokenAmount === null || sourceTokenAmount.isZero()) {
            return SwapButtonState.zeroAmount;
        }
        if (exceedingMaximumAmount) {
            return SwapButtonState.exceedingMaximumAmount;
        }
        if (isNativeToken(sourceToken.address)) {
            return SwapButtonState.wrapNativeToken;
        }
        if (loading && (isFirstCall || rate === null)) {
            return SwapButtonState.updateSwapData;
        }
        if (rate === null || rate.source === RateSource.onChain) {
            return SwapButtonState.rateNotExist;
        }
        if (sourceToken.chainId !== walletChainId) {
            return SwapButtonState.needChangeChainInWallet;
        }
        return SwapButtonState.readyToSwap;
    }
    async onClickSwapButton() {
        const handler = clickHandlers[this.buttonState];
        await handler(this);
        // if (!this.context) {
        //   throw new Error('')
        // }
        // const stateState = this.buttonState
        // try {
        //   if (this.buttonState === SwapButtonState.needChangeChainInWallet) {
        //     return dispatchEvent(this, 'changeChain', null)
        //   }
        //   if (this.buttonState === SwapButtonState.walletNotConnected) {
        //     return dispatchEvent(this, 'connectWallet', null)
        //   }
        //   if (this.buttonState === SwapButtonState.unselectedSourceToken) {
        //     return dispatchEvent(this, 'openTokenSelector', 'source', event)
        //   }
        //   if (this.buttonState === SwapButtonState.unselectedDestinationToken) {
        //     return dispatchEvent(this, 'openTokenSelector', 'destination', event)
        //   }
        //   if (this.buttonState === SwapButtonState.exceedingMaximumAmount) {
        //     return await this.context.setMaxAmount()
        //   }
        //   if (this.buttonState === SwapButtonState.lowAllowanceNeedApprove) {
        //     if (this.chainId === null) throw new Error('')
        //     this.buttonState = SwapButtonState.approveInWallet
        //     const hash = await this.context.getApprove()
        //     this.buttonState = SwapButtonState.waitingApproveTransaction
        //     await this.applicationContext.value.onChain.waitTransaction(this.chainId, hash)
        //     this.buttonState = SwapButtonState.readyToSwap
        //   }
        //   if (this.buttonState === SwapButtonState.lowAllowanceNeedPermit) {
        //     this.buttonState = SwapButtonState.permitInWallet
        //     await this.context.getPermit()
        //     this.buttonState = SwapButtonState.readyToSwap
        //   }
        //   if (this.buttonState === SwapButtonState.wrapNativeToken) {
        //     await this.applicationContext.value.notifications.warning(
        //       html`${translate('widgets.swap-form.swap-button.native-token-not-supported')}`
        //     )
        //   }
        //   if (this.buttonState === SwapButtonState.readyToSwap) {
        //     this.buttonState = SwapButtonState.readyToSwapLoading
        //     const snapshot = await this.context.getSnapshot()
        //     if (snapshot) {
        //       dispatchEvent(this, 'confirmSwap', snapshot, event)
        //     }
        //     this.buttonState = SwapButtonState.readyToSwap
        //   }
        // } catch {
        //   this.buttonState = stateState
        // }
    }
    getButtonType() {
        if (this.buttonState === SwapButtonState.readyToSwap)
            return 'primary';
        // if (this.buttonState === SwapButtonState.wrapNativeToken) return 'primary'
        if (this.buttonState === SwapButtonState.lowAllowanceNeedApprove)
            return 'primary';
        if (this.buttonState === SwapButtonState.lowAllowanceNeedPermit)
            return 'primary';
        return 'secondary';
    }
};
__decorate([
    property({ type: Boolean, attribute: false }),
    __metadata("design:type", Object)
], SwapButtonElement.prototype, "mobileView", void 0);
__decorate([
    state(),
    __metadata("design:type", Number)
], SwapButtonElement.prototype, "buttonState", void 0);
__decorate([
    CacheActivePromise(),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", []),
    __metadata("design:returntype", Promise)
], SwapButtonElement.prototype, "onClickSwapButton", null);
SwapButtonElement = __decorate([
    customElement(SwapButtonElement.tagName)
], SwapButtonElement);
export { SwapButtonElement };
//# sourceMappingURL=swap-button.element.js.map